// ================================================================
// ANALYTICS DASHBOARD COMPONENT
// Create new file: src/components/AnalyticsDashboard.js
// ================================================================

import React, { useState, useEffect } from 'react';
import { notes } from '../api';
import ThemeTrendsSection from './ThemeTrendsSection';
import TeamHoursTab from './Analytics/TeamHoursTab';
import TeachingsHub from './TeachingsHub';

const TABS = [
  { id: 'team', label: '👥 Team Hours' }, // head coach only
  { id: 'overview', label: '📊 Overview' },
  { id: 'rankings', label: '🏆 Rankings' },
  { id: 'milestones', label: '🎯 Milestones' },
  { id: 'notes', label: '📋 Note Stats' },
  { id: 'themes', label: '🎙️ Pocket Themes' },
  { id: 'teachings', label: '🎓 Teachings' },
];

// Pocket-based tabs (themes, teachings) are hidden when Pocket is off for this user
const POCKET_TABS = ['themes', 'teachings'];

function AnalyticsDashboard({ formatDateTime, userId, pocketEnabled = false, isHeadcoach = false }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState(isHeadcoach ? 'team' : 'overview');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await notes.getCoachAnalytics();
      setAnalytics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
        <div style={{ fontSize: '18px', color: '#7f8c8d' }}>Loading analytics...</div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
        <div style={{ fontSize: '18px', color: '#7f8c8d' }}>Failed to load analytics</div>
      </div>
    );
  }

  const { overview, milestones, rankings, noteStats } = analytics;

  return (
    <div style={{ padding: '20px', paddingBottom: '100px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* SUBMENU */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '2px solid #ecf0f1'
        }}>
          {TABS.filter(tab => (pocketEnabled || !POCKET_TABS.includes(tab.id)) && (isHeadcoach || tab.id !== 'team')).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 18px',
                borderRadius: '20px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                background: activeTab === tab.id ? '#2f4ba2' : '#ecf0f1',
                color: activeTab === tab.id ? 'white' : '#2c3e50',
                transition: 'background 0.15s ease, color 0.15s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
              padding: '24px',
              borderRadius: '16px',
              color: 'white',
              boxShadow: '0 4px 20px rgba(47, 75, 162, 0.3)'
            }}>
              <div style={{ fontSize: '14px', opacity: 1, marginBottom: '8px' }}>Total Students</div>
              <div style={{ fontSize: '36px', fontWeight: '700' }}>{overview.totalStudents}</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              padding: '24px',
              borderRadius: '16px',
              color: 'white',
              boxShadow: '0 4px 20px rgba(255, 215, 0, 0.3)'
            }} className="an-gold-card">
              <div style={{ fontSize: '14px', opacity: 1, marginBottom: '8px' }}>10+ Lessons</div>
              <div style={{ fontSize: '36px', fontWeight: '700' }}>{overview.studentsWithMilestones}</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
              padding: '24px',
              borderRadius: '16px',
              color: 'white',
              boxShadow: '0 4px 20px rgba(39, 174, 96, 0.3)'
            }}>
              <div style={{ fontSize: '14px', opacity: 1, marginBottom: '8px' }}>Read Rate</div>
              <div style={{ fontSize: '36px', fontWeight: '700' }}>{overview.overallReadRate}%</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
              padding: '24px',
              borderRadius: '16px',
              color: 'white',
              boxShadow: '0 4px 20px rgba(52, 152, 219, 0.3)'
            }}>
              <div style={{ fontSize: '14px', opacity: 1, marginBottom: '8px' }}>Total Notes</div>
              <div style={{ fontSize: '36px', fontWeight: '700' }}>{overview.totalNotesCreated}</div>
            </div>
          </div>
        )}

        {/* RANKINGS TAB */}
        {activeTab === 'rankings' && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '700' }}>
              🏆 Top Students (10+ Lessons)
            </h2>
            {rankings.length === 0 ? (
              <div style={{ color: '#7f8c8d', fontSize: '14px' }}>No students have reached 10 lessons yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {rankings.map(student => (
                  <div key={student.studentId} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    border: '2px solid #ecf0f1'
                  }}>
                    <div style={{ fontSize: '32px' }}>{student.badge}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '16px' }}>
                        #{student.rank} {student.studentName}
                      </div>
                      <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                        {student.lessonCount} lesson notes
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MILESTONES TAB */}
        {activeTab === 'milestones' && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '700' }}>
              🎯 Milestone Progress
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {milestones.map(student => (
                <div key={student.studentId} style={{
                  padding: '16px',
                  background: student.hasMilestone ? '#e8f5e9' : '#f8f9fa',
                  borderRadius: '12px',
                  border: `2px solid ${student.hasMilestone ? '#27ae60' : '#ecf0f1'}`
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <div style={{ fontWeight: '700', fontSize: '16px' }}>
                      {student.studentName}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: student.hasMilestone ? '#27ae60' : '#7f8c8d'
                    }}>
                      {student.lessonCount} / {student.nextMilestone}
                    </div>
                  </div>
                  <div style={{
                    height: '8px',
                    background: '#ecf0f1',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(student.lessonCount / student.nextMilestone) * 100}%`,
                      background: student.hasMilestone
                        ? 'linear-gradient(90deg, #27ae60 0%, #2ecc71 100%)'
                        : 'linear-gradient(90deg, #3498db 0%, #2f4ba2 100%)',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOTE STATS TAB */}
        {activeTab === 'notes' && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '700' }}>
              📋 Note Read Statistics
            </h2>

            {noteStats.map(student => (
              <div key={student.studentId} style={{ marginBottom: '24px' }}>
                <div
                  onClick={() => setSelectedStudent(
                    selectedStudent === student.studentId ? null : student.studentId
                  )}
                  style={{
                    padding: '16px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    border: '2px solid #ecf0f1'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '4px' }}>
                        {student.studentName}
                      </div>
                      <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                        {student.totalRead} / {student.totalNotes} read ({student.readPercentage}%)
                      </div>
                    </div>
                    <div style={{
                      background: student.readPercentage > 70 ? '#27ae60' :
                                  student.readPercentage > 40 ? '#f39c12' : '#e74c3c',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '700'
                    }}>
                      {student.readPercentage}%
                    </div>
                  </div>
                </div>

                {/* Expanded note details */}
                {selectedStudent === student.studentId && (
                  <div style={{
                    marginTop: '12px',
                    padding: '16px',
                    background: 'white',
                    borderRadius: '12px',
                    border: '2px solid #ecf0f1'
                  }}>
                    {student.notes.map(note => (
                      <div key={note.noteId} style={{
                        padding: '12px',
                        marginBottom: '8px',
                        background: note.isRead ? '#e8f5e9' : '#fff3e0',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${note.isRead ? '#27ae60' : '#f39c12'}`
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '8px'
                        }}>
                          <div style={{ fontSize: '14px', fontWeight: '600' }}>
                            {note.isRead ? '✅ Read' : '👁️ Viewed'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                            {note.viewCount} views
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', color: '#2c3e50' }}>
                          {note.noteText}
                        </div>
                        <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '4px' }}>
                          {new Date(note.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* TEAM HOURS TAB - head coach: every managed coach's hours, venues, customers, distribution */}
        {activeTab === 'team' && isHeadcoach && <TeamHoursTab />}

        {/* POCKET THEMES TAB */}
        {activeTab === 'themes' && (
          <ThemeTrendsSection mode="coach" coachId={userId} limit={15} />
        )}

        {/* TEACHINGS TAB */}
        {activeTab === 'teachings' && (
          <TeachingsHub coachId={userId} />
        )}

      </div>
    </div>
  );
}

export default AnalyticsDashboard;
